import { RecentPurchase } from '@usf/list-types';
import { EasyPricingViewModel, LastBuy } from '../../models/easy-pricing.model';
import { PmeCprRationale, PmeCurrentPrice } from '@usf/price-types';
import {
  EasyPricingState,
  LoadingState,
  ProcessingState,
} from '@usf/ngrx-pricing';
import { Customer } from '@usf/customer-types';
import { Dictionary } from '@ngrx/entity';
import { roundPrice } from '@product-detail/utils/cpr.util';
import { getEasyPricingStatus } from '../../utils/easy-pricing-status.util';

export function calculateRationale(
  cprRationale: PmeCprRationale | undefined,
  cprAutoAssist: string,
): string {
  if (!cprRationale) {
    return '';
  }
  return `${cprRationale.priceBasis}${cprRationale.upDown}${roundPrice(cprRationale.markupAmount)}${cprRationale.markupType} ${cprAutoAssist}`;
}

export function calculateWwp(purchaseDtm: Date | string): string {
  if (!purchaseDtm) return '';

  const currentDate = new Date();
  const lastPurchaseDate = new Date(purchaseDtm);
  const diffTime = Math.abs(currentDate.getTime() - lastPurchaseDate.getTime());
  const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));

  return diffWeeks < 1 ? 'curr' : diffWeeks.toString();
}

export function calculateLastBuys(recentPurchase: RecentPurchase): LastBuy[] {
  const lastBuys: LastBuy[] = [];

  const createLastBuyEntry = (
    purchaseDtm: Date | string,
    invoiceNumber: number | undefined,
    price: number,
    qty: number,
  ) => {
    if (purchaseDtm) {
      lastBuys.push({
        invoiceDate: purchaseDtm,
        invoiceNumber: invoiceNumber,
        wwp: calculateWwp(purchaseDtm),
        price,
        qty,
      });
    }
  };

  createLastBuyEntry(
    recentPurchase.purchaseDtm1,
    recentPurchase.invoiceNumber1,
    recentPurchase.purchasePrice1,
    recentPurchase.unitsOrdered1,
  );

  createLastBuyEntry(
    recentPurchase.purchaseDtm2,
    recentPurchase.invoiceNumber2,
    recentPurchase.purchasePrice2,
    recentPurchase.unitsOrdered2,
  );

  createLastBuyEntry(
    recentPurchase.purchaseDtm3,
    recentPurchase.invoiceNumber3,
    recentPurchase.purchasePrice3,
    recentPurchase.unitsOrdered3,
  );

  createLastBuyEntry(
    recentPurchase.purchaseDtm4,
    recentPurchase.invoiceNumber4,
    recentPurchase.purchasePrice4,
    recentPurchase.unitsOrdered4,
  );

  return lastBuys;
}

export function calculateTargetDifference(
  currentPrice: PmeCurrentPrice | undefined,
): number {
  if (
    currentPrice?.unitPrice === undefined ||
    currentPrice?.cookbookPrices?.cookbookTargetPrice === undefined
  ) {
    return 0;
  }
  if (currentPrice.autoShippingCharges?.ascApplyFlag === 'P') {
    return roundPrice(
      currentPrice.autoShippingCharges.unitPriceBeforeAsc -
        currentPrice.cookbookPrices.cookbookTargetPrice,
    );
  }
  return roundPrice(
    currentPrice.unitPrice - currentPrice.cookbookPrices.cookbookTargetPrice,
  );
}

export function calculateCurrentWeekDifference(
  currentPrice: number | undefined,
  lastPrice: number | undefined,
): number {
  if (currentPrice === undefined || lastPrice === undefined) {
    return 0;
  }
  return roundPrice(currentPrice - lastPrice);
}

export function calculateTargetCasePrice(
  cookbookTargetPrice: number | undefined,
  ascApplyFlag: string | undefined,
  unitAscRate: number | undefined,
): number {
  if (cookbookTargetPrice === undefined) {
    return 0;
  }

  if (ascApplyFlag === 'P' && unitAscRate !== undefined) {
    return roundPrice(cookbookTargetPrice + unitAscRate);
  }

  return roundPrice(cookbookTargetPrice);
}

export function calculateLowLimit(
  cprLowLimit: number | undefined,
  ascApplyFlag: string | undefined,
  unitAscRate: number | undefined,
): number {
  if (cprLowLimit === undefined) {
    return 0;
  }

  if (ascApplyFlag === 'P' && unitAscRate !== undefined) {
    return roundPrice(cprLowLimit + unitAscRate);
  }

  return roundPrice(cprLowLimit);
}

export function calculateAsc(
  ascApplyFlag: string | undefined,
  unitAscRate: number | undefined,
): number {
  if (ascApplyFlag === 'N' || unitAscRate === undefined) {
    return 0;
  }
  return roundPrice(unitAscRate);
}

interface DriverInfo {
  driverNumber?: number;
  driverName: string;
}

export function calculateDriverInfo(
  currentPrice: PmeCurrentPrice | undefined,
  customerEntities: Dictionary<Customer>,
): DriverInfo {
  if (
    !currentPrice?.priceControls?.passengerCprInd ||
    currentPrice.priceControls.passengerCprInd !== 'Y'
  ) {
    return {
      driverNumber: undefined,
      driverName: '',
    };
  }
  const driverNumber = currentPrice.concprNumber;
  const customer = customerEntities
    ? customerEntities[driverNumber]
    : undefined;
  return {
    driverNumber,
    driverName: customer?.customerName ?? '',
  };
}

export function calculateLoadingStates(easyPricing: EasyPricingState): {
  isLoading: boolean;
  isReloading: boolean;
  isProcessing: boolean;
  isError: boolean;
  isSuccess: boolean;
} {
  return {
    isLoading: easyPricing.loadingState === LoadingState.loading,
    isReloading: easyPricing.loadingState === LoadingState.reloading,
    isProcessing: easyPricing.processingState === ProcessingState.processing,
    isError: easyPricing.processingState === ProcessingState.error,
    isSuccess: easyPricing.processingState === ProcessingState.success,
  };
}

export function calculateIsCprApproved(easyPricing: EasyPricingState): boolean {
  const approvalStatus = easyPricing?.manualCpr?.approvalStatus?.toUpperCase();
  return approvalStatus === 'APPROVED' || approvalStatus === 'MODIFIED';
}

export function createEasyPricingViewModel(
  easyPricing: EasyPricingState | undefined,
  recentPurchase: RecentPurchase | undefined,
  customerEntities: Dictionary<Customer>,
): EasyPricingViewModel | null {
  if (!easyPricing?.currentPrice) {
    return easyPricing;
  }

  const { currentPrice, lastPrice, zilliantDetails } = easyPricing;
  const { driverNumber, driverName } = calculateDriverInfo(
    currentPrice,
    customerEntities,
  );

  return {
    ...easyPricing,
    targetDifference: calculateTargetDifference(currentPrice),
    currentWeekDifference: calculateCurrentWeekDifference(
      currentPrice.unitPrice,
      lastPrice?.unitPrice,
    ),
    targetCasePrice: calculateTargetCasePrice(
      currentPrice.cookbookPrices?.cookbookTargetPrice,
      currentPrice.autoShippingCharges?.ascApplyFlag,
      currentPrice.autoShippingCharges?.unitAscRate,
    ),
    rationale: calculateRationale(
      currentPrice.cprRationale,
      zilliantDetails?.cprAutoAssist ?? '',
    ),
    wwp: recentPurchase ? calculateWwp(recentPurchase.purchaseDtm1) : '',
    lowLimit: calculateLowLimit(
      zilliantDetails?.cprLowLimit,
      currentPrice.autoShippingCharges?.ascApplyFlag,
      currentPrice.autoShippingCharges?.unitAscRate,
    ),
    lastBuys: recentPurchase ? calculateLastBuys(recentPurchase) : [],
    asc: calculateAsc(
      currentPrice.autoShippingCharges?.ascApplyFlag,
      currentPrice.autoShippingCharges?.unitAscRate,
    ),
    driverNumber,
    driverName,
    isCprEnabled: currentPrice.priceControls?.cprSetupInd === 'Y',
    ...calculateLoadingStates(easyPricing),
    priceStatus: getEasyPricingStatus(easyPricing),
    isCprApproved: calculateIsCprApproved(easyPricing),
    currentUnitPrice: roundPrice(currentPrice.unitPrice ?? 0),
    lastUnitPrice: roundPrice(lastPrice?.unitPrice ?? 0),
    cprLowLimit: roundPrice(zilliantDetails?.cprLowLimit ?? 0),
    currentGrossProfitDollars: roundPrice(currentPrice.grossProfitDollars ?? 0),
    currentGrossProfitPercent: roundPrice(currentPrice.grossProfitPercent ?? 0),
    currentMarketChange: roundPrice(currentPrice.marketChange ?? 0),
    currentBreakSurchargeAmt: roundPrice(currentPrice.breakSurchargeAmt ?? 0),
    autoShippingCharges: roundPrice(
      currentPrice.autoShippingCharges?.unitAscRate ?? 0,
    ),
    currentEachPrice: roundPrice(currentPrice.eachPrice ?? 0),
  };
}
